<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('task.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('task.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="ts-flex ts-justify-between">
          <div class="ts-flex ts-space-x-3">
            <ts-button outline color="info" @click.prevent="addNew">
              {{ $t('add_new') }}
            </ts-button>
            <a-range-picker
              v-model="estimateDate"
              format="DD-MM-YYYY"
              valueFormat="YYYY-MM-DD"
              style="width: 200px;"
              :placeholder="['Estimate Start', 'Estimate End']"
              @change="fetchData()"
            />
          </div>
          <div>
            <Tooltip placement="left" max-width="300">
              <Icon type="md-help-circle" size="20" />
              <template #content>
                <p class="ts-text-xs">{{ $t('task.help') }}</p>
              </template>
            </Tooltip>
          </div>
        </div>
      </ts-card-wrapper>
      <ts-table-filter
        :columns="columns"
        :data="resources"
        :loading="loading"
        @search="search"
      >
        <template slot-scope="{ row }" slot="title">
          <a
            href="#"
            @click.prevent="onViewTask(row)"
            class="ts-mb-0 ts-no-underline hover:ts-no-underline"
            >{{ row.title }}</a
          >
        </template>
        <template slot-scope="{ row }" slot="status">
          <div class="ts-group">
            <select
              v-model="row.status_id"
              class="ts-py-1 ts-px-2 ts-rounded ts-outline-none group-hover:ts-hidden hide-select ts-w-32 ts-bg-transparent"
              readonly
            >
              <option
                v-for="(s, index) in status"
                :key="index"
                :value="s.status_id"
                >{{ s.project_status }}</option
              >
            </select>
            <div class="group-hover:ts-block ts-hidden">
              <select
                v-model="row.status_id"
                class="ts-py-1 ts-px-2 ts-rounded ts-outline-none ts-w-32 hide-select"
                @change="updateColumnValueWhenChange(row, 'status_id')"
              >
                <option
                  v-for="(s, index) in status"
                  :key="index"
                  :value="s.status_id"
                  >{{ s.project_status }}</option
                >
              </select>
            </div>
          </div>
        </template>
        <template slot-scope="{ row }" slot="priority">
          <div class="ts-group">
            <div
              class="ts-flex ts-space-x-2 ts-items-center group-hover:ts-hidden"
            >
              <div
                class="ts-h-2 ts-w-2 ts-rounded-full"
                :class="[priorities[row.priority_id].color]"
              ></div>
              <span>{{ priorities[row.priority_id].label }}</span>
            </div>
            <div class="group-hover:ts-block ts-hidden">
              <select
                v-model="row.priority_id"
                class="ts-py-1 ts-px-2 ts-rounded ts-outline-none ts-w-32 hide-select"
                @change="updateColumnValueWhenChange(row, 'priority_id')"
              >
                <option v-for="(p, id) in priorities" :key="id" :value="id">{{
                  p.label
                }}</option>
              </select>
            </div>
          </div>
        </template>
        <template slot-scope="{ row }" slot="owner">
          <div class="ts-group">
            <span class="group-hover:ts-hidden">{{
              row.owner_name ? row.owner_name : '-'
            }}</span>
            <div
              class="ts-relative group-hover:ts-block ts-hidden"
              style="z-index: 1024;"
            >
              <div class="ts-absolute ts-top-0 ts--mt-4 ts-z-50">
                <div
                  class="ts-fixed ts-bg-white ts-p-2 ts-rounded-md ts-border ts-border-blue-100"
                  style="z-index: "
                >
                  <Select
                    v-model="row.owner_id"
                    :filterable="true"
                    style="width:20rem"
                    @on-change="
                      updateColumnValueWhenChange(row, 'owner_id', true)
                    "
                  >
                    <Option
                      v-for="item in owners"
                      :value="item.user_id"
                      :key="item.user_id"
                      >{{
                        item.employee_name_kh + ' ' + item.employee_name_en
                      }}</Option
                    >
                  </Select>
                </div>
              </div>
            </div>
          </div>
        </template>
        <template slot-scope="{ row, index }" slot="estimated">
          <div @mouseover="onMouseover('show_index_est_date', index)">
            <div
              style="width: fit-content;"
              class="group-hover:ts-hidden ts-block"
              v-if="show_index_est_date !== index"
            >
              <Icon type="ios-calendar-outline ts-mr-1" />
              <span>{{ row.estimate_start_date }}</span>
              <Icon type="ios-calendar-outline ts-mr-1 ts-ml-2" />
              <span>{{ row.estimate_end_date }}</span>
            </div>
            <a-range-picker
              v-if="index === show_index_est_date"
              :value="[row.estimate_start_date, row.estimate_end_date]"
              format="DD-MM-YYYY"
              valueFormat="DD-MM-YYYY"
              class="ts-w-full"
              @change="
                value =>
                  updateColumnValueWhenChange(
                    { ...value, ...row },
                    ['estimate_start_date', 'estimate_end_date'],
                    true
                  )
              "
            />
          </div>
        </template>
        <template slot-scope="{ row, index }" slot="actual">
          <div @mouseover="onMouseover('show_index_act_date', index)">
            <div
              style="width: fit-content;"
              class="group-hover:ts-hidden ts-block"
              v-if="show_index_act_date !== index"
            >
              <Icon type="ios-calendar-outline ts-mr-1" />
              <span>{{ row.actual_start_date }}</span>
              <Icon type="ios-calendar-outline ts-mr-1 ts-ml-2" />
              <span>{{ row.actual_end_date }}</span>
            </div>
            <a-range-picker
              v-if="index === show_index_act_date"
              :value="[row.actual_start_date, row.actual_end_date]"
              format="DD-MM-YYYY"
              valueFormat="DD-MM-YYYY"
              class="ts-w-full"
              @change="
                value =>
                  updateColumnValueWhenChange(
                    { ...value, ...row },
                    ['actual_start_date', 'actual_end_date'],
                    true
                  )
              "
            />
          </div>
        </template>
        <template slot-scope="{ row }" slot="remark">
          {{ limitString(row.remarks) }}
        </template>
        <template slot-scope="{ row }" slot="action">
          <div class="ts-flex ts-space-x-2" style="width: fit-content;">
            <a
              href="#"
              class="text-primary"
              @click.prevent="onEdit(row)"
              v-tooltip="$t('edit')"
            >
              <Icon type="ios-create" size="20" />
            </a>
            <a
              href="#"
              class="text-danger"
              v-tooltip="$t('delete')"
              @click="deleteRecord(row)"
              :disabled="row._deleting"
            >
              <i class="fas fa-circle-notch fa-spin" v-if="row._deleting"></i>
              <Icon type="ios-trash" size="20" v-else />
            </a>
          </div>
        </template>
      </ts-table-filter>
      <div class="ts-flex ts-items-center ts-justify-end ts-p-4">
        <ts-i-pagination
          v-model="pagination"
          @navigate="fetchData"
        ></ts-i-pagination>
      </div>
    </ts-card>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import moment from 'moment'
import { mapActions, mapState } from 'vuex'
export default {
  data() {
    return {
      loading: false,
      isOpen: false,
      show_index_est_date: undefined,
      show_index_act_date: undefined,
      status: [],
      owners: []
    }
  },
  computed: {
    ...mapState('projectMgt/taskProfile', ['resources', 'pagination']),
    limitString: () => (text, limit = 20) => {
      if (text != null) {
        let article = text.replace(/(<([^>]+)>)/gi, '')
        return article.length > limit
          ? article.substring(0, limit) + '...'
          : article
      }
      return ''
    },
    onlyMyTask: {
      get() {
        return this.$store.state.projectMgt.taskProfile.only_my_task
      },
      set(newValue) {
        this.$store.commit(
          'projectMgt/taskProfile/SET_MY_TASK_FILTER',
          newValue
        )
      }
    },
    involveTask: {
      get() {
        return this.$store.state.projectMgt.taskProfile.involve_task
      },
      set(newValue) {
        this.$store.commit(
          'projectMgt/taskProfile/SET_INVOLVE_TASK_FILTER',
          newValue
        )
      }
    },
    createdByMe: {
      get() {
        return this.$store.state.projectMgt.taskProfile.created_by_me
      },
      set(newValue) {
        this.$store.commit(
          'projectMgt/taskProfile/SET_CREATED_BY_ME_FILTER',
          newValue
        )
      }
    },
    prioritiesId: {
      get() {
        return this.$store.state.projectMgt.taskProfile.priorities
      },
      set(newValue) {
        this.$store.commit(
          'projectMgt/taskProfile/SET_PRIORITY_FILTER',
          newValue
        )
      }
    },
    statusId: {
      get() {
        return this.$store.state.projectMgt.taskProfile.statusId
      },
      set(newValue) {
        this.$store.commit('projectMgt/taskProfile/SET_STATUS_FILTER', newValue)
      }
    },    
    estimateDate: {
      get() {
        return this.$store.state.projectMgt.taskProfile.estimateDate
      },
      set(newValue) {
        this.$store.commit('projectMgt/taskProfile/SET_ESTIMATE_DATE', newValue)
      }
    },
    priorities() {
      return {
        1: {
          label: 'Low',
          color: 'ts-bg-gray-500'
        },
        2: {
          label: 'Normal',
          color: 'ts-bg-blue-500'
        },
        3: {
          label: 'Meduim',
          color: 'ts-bg-yellow-500'
        },
        4: {
          label: 'Hight',
          color: 'ts-bg-red-600'
        }
      }
    },
    columns() {
      return [
        {
          title: this.$t('task.title'),
          slot: 'title',
          keySearch: 'title',
          minWidth: 200
        },
        {
          title: this.$t('task.project'),
          key: 'project_name',
          keySearch: 'project_name',
          minWidth: 200
        },
        {
          title: this.$t('task.status'),
          slot: 'status',
          keySearch: 'status.project_status',
          minWidth: 120
          // fixed: 'left'
        },
        {
          title: this.$t('task.priority'),
          slot: 'priority',
          keySearch: 'pri.label',
          minWidth: 120
          // fixed: 'left'
        },
        {
          title: this.$t('task.owner'),
          slot: 'owner',
          keySearch: 'emp.employee_name_en',
          minWidth: 200
          // fixed: 'left'
        },
        {
          title: this.$t('task.estimated'),
          slot: 'estimated',
          minWidth: 220
        },
        {
          title: this.$t('task.actual'),
          slot: 'actual',
          minWidth: 220,
          className: 'ts-font-robot ts-whitespace-no-wrap'
        },
        {
          title: this.$t('task.createdAt'),
          key: 'created_at',
          minWidth: 200,
          className: 'ts-font-robot ts-whitespace-no-wrap'
        },
        {
          title: this.$t('task.createdBy'),
          key: 'created_by_name',
          keySearch: 'empc.employee_name_en',
          minWidth: 120
        },
        {
          title: this.$t('task.milestone'),
          key: 'milestone_name',
          keySearch: 'milestone_name',
          minWidth: 200
        },
        {
          title: this.$t('task.remark'),
          slot: 'remark',
          keySearch: 'remarks',
          minWidth: 200
        },
        {
          title: this.$t('actions'),
          slot: 'action',
          fixed: 'right',
          align: 'left',
          width: 130
        }
      ]
    }
  },
  created() {
    this.fetchData()
    this.fetchModel()
  },
  methods: {
    ...mapActions('projectMgt/taskProfile', [
      'fetch',
      'destroy',
      'formViewModel',
      'updateColumnValue'
    ]),
    search(newValue) {
      this.$store.commit('projectMgt/taskProfile/SET_SEARCH', newValue)
      this.$store.commit('projectMgt/taskProfile/RESET_CURRENT_PAGE')
      this.fetchData()
    },
    addNew() {
      this.$router.push({ name: 'task-create' })
    },
    updateColumnValueWhenChange(row, columns, isNeedFetch = false) {
      let model = {}
      if (Array.isArray(columns)) {
        columns.forEach((el, index) => {
          model[el] = moment(row[index], 'DD-MM-YYYY').isValid()
            ? moment(row[index], 'DD-MM-YYYY').format('YYYY-MM-DD')
            : row[index]
        })
      } else {
        model[columns] = row[columns]
      }
      if (!Object.keys(model).length) return
      this.updateColumnValue({
        id: row.id,
        data: model
      })
        .then(response => {
          this.$toasted.success(response.message)
          if (isNeedFetch) {
            this.fetch()
          }
          this.show_index_est_date = undefined
          this.show_index_act_date = undefined
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => (this.loading = false))
    },
    fetchData(attributes) {
      this.loading = true
      this.fetch(attributes)
        .then(() => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    fetchModel() {
      this.formViewModel({
        fnName: 'status,owner'
      })
        .then(response => {
          this.status = response.data.status
          this.owners = response.data.owner
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
    },
    onEdit(record) {
      this.$router.push({
        name: 'task-edit',
        params: {
          id: record.id
        }
      })
    },
    deleteRecord(record) {
      this.$confirm({
        title: this.$t('are_you_sure_to_delete'),
        okText: this.$t('yes'),
        okType: 'danger',
        cancelText: this.$t('no'),
        centered: true,
        onOk: () => {
          return this.destroy(record.id)
            .then(response => {
              this.$toasted.success(response.message)
              this.fetchData()
              return Promise.resolve(response)
            })
            .catch(error => {
              this.$toasted.error(error.message)
              return Promise.reject(error)
            })
        },
        onCancel() {}
      })
    },
    onViewTask(record) {
      this.$router.push({
        name: 'task-view',
        params: {
          id: record.id
        }
      })
    },
    handleSelectAll(value, row) {
    },
    onSelect(value) {
    },
    onSelectUncheck(value) {
    },
    onSelectAllUncheck(value) {
    },
    onMouseover(key, index) {
      this.show_index_act_date = undefined
      this.show_index_est_date = undefined
      this[key] = index
    }
  },
  watch: {
    search: debounce(function() {
      this.fetchData()
    }, 500)
  }
}
</script>

<style scoped>
.hide-select {
  -webkit-appearance: none;
  -moz-appearance: none;
  text-indent: 1px;
  text-overflow: '';
}
</style>
